// shared-components/lib/themeUtils.js

/**
 * Generates CSS variables for a theme's color palette
 * @param {Object} theme The theme configuration object
 * @returns {Object} Light and dark mode CSS variable definitions
 */
export function generateCssVariables(theme) {
  if (!theme || !theme.colors) return { light: "", dark: "" };

  const lightVars = [];
  const darkVars = [];

  // Process each color category (primary, secondary, accent)
  Object.entries(theme.colors).forEach(([category, shades]) => {
    // Process each shade (50, 100, 200, etc.)
    Object.entries(shades).forEach(([shade, value]) => {
      // Convert hex to RGB for better Tailwind support
      const rgb = hexToRgb(value);
      if (rgb) {
        // Store as RGB values for Tailwind opacity support
        lightVars.push(
          `--color-${category}-${shade}: ${rgb.r} ${rgb.g} ${rgb.b};`,
        );
        darkVars.push(
          `--color-${category}-${shade}: ${rgb.r} ${rgb.g} ${rgb.b};`,
        );
      }
    });

    // Add shadcn/ui compatible variables if this is primary, secondary, or accent
    if (["primary", "secondary", "accent"].includes(category)) {
      // Use the 500 shade as the DEFAULT for shadcn compatibility
      if (shades["500"]) {
        const hsl = hexToHSL(shades["500"]);
        if (hsl) {
          lightVars.push(`--${category}: ${hsl};`);
          darkVars.push(`--${category}: ${hsl};`);

          // Add foreground variables for text contrast
          // For now using a simple approach - we could make this more sophisticated later
          const foreground =
            category === "primary" || category === "accent"
              ? "0 0% 100%"
              : "0 0% 9%";
          lightVars.push(`--${category}-foreground: ${foreground};`);
          darkVars.push(`--${category}-foreground: ${foreground};`);
        }
      }
    }
  });

  return {
    light: lightVars.join("\n  "),
    dark: darkVars.join("\n  "),
  };
}

/**
 * Helper function to convert hex color to HSL format for CSS variables
 * @param {string} hex The hex color code
 * @returns {string|null} HSL values as a string or null if invalid
 */
function hexToHSL(hex) {
  try {
    // Convert hex to RGB
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;

    // Find greatest and smallest channel values
    const cmin = Math.min(r, g, b);
    const cmax = Math.max(r, g, b);
    const delta = cmax - cmin;

    let h = 0;
    let s = 0;
    let l = 0;

    // Calculate hue
    if (delta === 0) h = 0;
    else if (cmax === r) h = ((g - b) / delta) % 6;
    else if (cmax === g) h = (b - r) / delta + 2;
    else h = (r - g) / delta + 4;

    h = Math.round(h * 60);
    if (h < 0) h += 360;

    // Calculate lightness
    l = (cmax + cmin) / 2;

    // Calculate saturation
    s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));

    // Convert to percentages
    s = Math.round(s * 100);
    l = Math.round(l * 100);

    return `${h} ${s}% ${l}%`;
  } catch {
    console.warn("Invalid hex color in theme:", hex);
    return null;
  }
}

function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

/**
 * Creates a theme style element and injects it into the document head
 * @param {Object} theme The theme configuration object
 */
export function applyThemeToDocument(theme) {
  if (typeof document === "undefined") return; // Skip during SSR

  // Remove any existing theme style element
  const existingTheme = document.getElementById("site-theme-variables");
  if (existingTheme) {
    existingTheme.remove();
  }

  // Create and inject the new theme stylesheet
  const styleEl = document.createElement("style");
  styleEl.id = "site-theme-variables";

  const { light, dark } = generateCssVariables(theme);

  // Add base variables that should be consistent across sites but adapt to theme
  const baseVars = `
    --background: 0 0% 100%;
    --foreground: 222.2 47.4% 11.2%;
    --radius: 0.5rem;

    --destructive: 0 100% 50%;
    --destructive-foreground: 210 40% 98%;

    --ring: 215 20.2% 65.1%;
    --border: 214.3 31.8% 91.4%;
    --input: 214.3 31.8% 91.4%;

    --muted: 210 40% 96.1%;
    --muted-foreground: 215.4 16.3% 46.9%;

    --popover: 0 0% 100%;
    --popover-foreground: 222.2 47.4% 11.2%;

    --card: 0 0% 100%;
    --card-foreground: 222.2 47.4% 11.2%;
    `;

  // Dark mode base variables
  const baseDarkVars = `
    --background: 224 71% 4%;
    --foreground: 213 31% 91%;

    --destructive: 0 63% 31%;
    --destructive-foreground: 210 40% 98%;

    --ring: 216 34% 17%;
    --border: 216 34% 17%;
    --input: 216 34% 17%;

    --muted: 223 47% 11%;
    --muted-foreground: 215.4 16.3% 56.9%;

    --popover: 224 71% 4%;
    --popover-foreground: 215 20.2% 65.1%;

    --card: 224 71% 4%;
    --card-foreground: 213 31% 91%;
    `;

  styleEl.textContent = `
    :root {
    ${baseVars}
    ${light}
    }

    .dark {
    ${baseDarkVars}
    ${dark}
    }
    `;

  document.head.appendChild(styleEl);
}

/**
 * Update your Tailwind config to use the CSS variables
 * This is a helper function that would be used when setting up the project
 */
export function getTailwindThemeConfig() {
  // This function would return a configuration object for tailwind.config.js
  // to use the CSS variables defined above
  return {
    theme: {
      extend: {
        colors: {
          primary: {
            50: "var(--color-primary-50)",
            100: "var(--color-primary-100)",
            200: "var(--color-primary-200)",
            300: "var(--color-primary-300)",
            400: "var(--color-primary-400)",
            500: "var(--color-primary-500)",
            600: "var(--color-primary-600)",
            700: "var(--color-primary-700)",
            800: "var(--color-primary-800)",
            900: "var(--color-primary-900)",
          },
          secondary: {
            50: "var(--color-secondary-50)",
            100: "var(--color-secondary-100)",
            200: "var(--color-secondary-200)",
            300: "var(--color-secondary-300)",
            400: "var(--color-secondary-400)",
            500: "var(--color-secondary-500)",
            600: "var(--color-secondary-600)",
            700: "var(--color-secondary-700)",
            800: "var(--color-secondary-800)",
            900: "var(--color-secondary-900)",
          },
          accent: {
            50: "var(--color-accent-50)",
            100: "var(--color-accent-100)",
            200: "var(--color-accent-200)",
            300: "var(--color-accent-300)",
            400: "var(--color-accent-400)",
            500: "var(--color-accent-500)",
            600: "var(--color-accent-600)",
            700: "var(--color-accent-700)",
            800: "var(--color-accent-800)",
            900: "var(--color-accent-900)",
          },
        },
      },
    },
  };
}
